import cx from 'classnames';
import {Form, Formik, FormikProps} from 'formik';
import {get, map, set, transform, values} from 'lodash';
import moment from 'moment-timezone';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {settingsApi} from 'api';
import {
    Button, DataWrapper, FileArea, FileOwner, FilesManagerInput, InputFormik, Loader,
    PredefinedNotifierType, SelectFormik, UnsavedChangesLeavePrompt
} from 'components';
import {DateFormat, TimeFormat} from 'enums';
import {useApiData} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {AccountSettingsFormFields} from 'types';
import {languageUtils, selectUtils, userAuthUtils} from 'utils';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.SETTINGS;
const COMMON_T_KEYS = TRANSLATION_KEYS.COMMON;

const ACCOUNT_FIELDS = {
    ID: 'id',
    NAME: 'name',
    TIMEZONE: 'timeZone',
    TIME_FORMAT: 'timeFormat',
    DATE_FORMAT: 'dateFormat',
    LANGUAGE: 'language',
    LOGO: 'logo'
};

type TemplateProps = {
    loading: boolean;
    reloadRequired: boolean;
} & FormikProps<AccountSettingsFormFields>

const FormTemplate = ({loading, dirty, reloadRequired, values: formValues, initialValues, setFieldValue}: TemplateProps) => {
    const {t} = useTranslation();
    const [logoError, setLogoError] = useState(false);
    const [logoLoading, setLogoLoading] = useState(false);
    const [timeZoneFilter, setTimeZoneFilter] = useState<string>('');
    const isSaveDisabled = useMemo<boolean>(() =>
        !formValues.name || logoError || !dirty, [formValues.name, logoError, dirty]);
    const [logoModalOpen, setLogoModalOpen] = useState(false);

    const handleAccountLogoUpdate = useCallback((value: string) => {
        setFieldValue(ACCOUNT_FIELDS.LOGO, value);
        setLogoLoading(!!value);

        if (!value) {
            setLogoError(false);
        }
    }, [setFieldValue]);

    const languageOptions = useMemo(() => languageUtils.getLanguageOptions(t), [t]);

    const timeZoneOptions = useMemo(() => selectUtils.getFilteredOptions(
        formValues.timeZone,
        map(moment.tz.names(), (name) => ({id: name, name})),
        timeZoneFilter
    ), [timeZoneFilter, formValues.timeZone]);

    const timeFormatOptions = useMemo(() => [
        {id: TimeFormat.TwentyFour, name: t(T_KEYS.TIME_FORMAT.TWENTY_FOUR_HOURS)},
        {id: TimeFormat.Twelve, name: t(T_KEYS.TIME_FORMAT.TWELVE_HOURS)}
    ], [t]);

    const dateFormatOptions = useMemo(() => map(DateFormat, (value) => ({id: value, name: value})), []);

    const handleBlurLogoInput = useCallback(() => {
        if (!formValues.logo) {
            setLogoError(false);
        }
    }, [formValues.logo]);

    const handleLogoLoaded = useCallback(() => {
        setLogoError(false);
        setLogoLoading(false);
    }, []);

    const handleLogoError = useCallback(() => {
        setLogoError(true);
        setLogoLoading(false);
    }, []);

    return (
        <Form>
            <div className='md:tw-flex'>
                <div className='tw-flex-grow'>
                    <div className='tw-flex tw-flex-col tw-mb-4'>
                        <div className='tw-flex tw-justify-between'>
                            <InputFormik className='tw-flex-grow' name={ACCOUNT_FIELDS.ID} label={t(COMMON_T_KEYS.ALL.ID)} disabled />
                            <InputFormik
                                className='tw-flex-grow tw-ml-4'
                                name={ACCOUNT_FIELDS.NAME}
                                label={t(COMMON_T_KEYS.ALL.NAME)}
                                required
                            />
                        </div>
                        <div className='tw-flex -lg:tw-flex-col tw-mt-4 tw-min-w-48 lg:tw-mt-4'>
                            <div className='tw-flex tw-flex-grow lg:tw-w-1/2'>
                                <SelectFormik
                                    className='tw-flex-grow'
                                    placeholder={t(COMMON_T_KEYS.ALL.DATE_FORMAT)}
                                    name={ACCOUNT_FIELDS.DATE_FORMAT}
                                    value={formValues.dateFormat}
                                    options={dateFormatOptions}
                                    valueAs='id'
                                />
                                <SelectFormik
                                    className='tw-flex-grow tw-ml-4'
                                    placeholder={t(COMMON_T_KEYS.ALL.TIME_FORMAT)}
                                    name={ACCOUNT_FIELDS.TIME_FORMAT}
                                    value={formValues.timeFormat}
                                    options={timeFormatOptions}
                                    valueAs='id'
                                />
                            </div>
                            <div className='tw-flex tw-flex-grow lg:tw-w-1/2 -lg:tw-mt-4 lg:tw-ml-4'>
                                <SelectFormik
                                    className='tw-flex-grow'
                                    isSearchable
                                    placeholder={t(COMMON_T_KEYS.ALL.TIMEZONE)}
                                    name={ACCOUNT_FIELDS.TIMEZONE}
                                    value={formValues.timeZone}
                                    options={timeZoneOptions}
                                    valueAs='id'
                                    onInputChange={setTimeZoneFilter}
                                />
                                <SelectFormik
                                    className='tw-flex-grow tw-ml-4'
                                    placeholder={t(COMMON_T_KEYS.ALL.LANGUAGE)}
                                    name={ACCOUNT_FIELDS.LANGUAGE}
                                    value={formValues.language}
                                    options={languageOptions}
                                    valueAs='id'
                                />
                            </div>
                        </div>
                        <div className='tw-flex tw-flex-grow tw-mt-4 -lg:tw-mt-4 -lg:tw-flex-col'>
                            <FilesManagerInput
                                modalOpen={logoModalOpen}
                                filesManagerProps={{
                                    area: FileArea.AccountLogo,
                                    ownerType: FileOwner.Account,
                                }}
                                inputProps={{
                                    name: ACCOUNT_FIELDS.LOGO,
                                    transparent: true,
                                    label: t(COMMON_T_KEYS.ALL.LOGO),
                                    value: formValues.logo || '',
                                    hasError: logoError,
                                    errorMessage: t(T_KEYS.LOGO_ERROR),
                                    onBlur: handleBlurLogoInput,
                                    onChange: handleAccountLogoUpdate,
                                }}
                                onFilePathSelect={handleAccountLogoUpdate}
                                onCloseModal={() => setLogoModalOpen(false)}
                            />
                        </div>
                    </div>
                </div>
                <div
                    className='tw-relative md:tw-w-52 tw-max-h-36 tw-min-h-16 tw-flex-center -md:tw-mt-6 md:tw-mt-4 md:tw-mx-8
                        tw-cursor-pointer tw-border tw-border-default-dimmed tw-border-dashed'
                    onClick={() => setLogoModalOpen(true)}
                >
                    {!formValues.logo && !logoLoading &&
                        <div className='tw-w-full tw-flex-center tw-text-default-dimmed tw-text-lg tw-h-full -md:tw-min-h-32'>
                            {t(T_KEYS.LOGO_PLACEHOLDER)}
                        </div>}
                    {formValues.logo &&
                        <img
                            className={cx('tw-h-7 tw-m-2', {'tw-hidden': logoLoading})}
                            src={formValues.logo}
                            onLoad={handleLogoLoaded}
                            onError={handleLogoError}
                        />}
                    {logoLoading && <Loader svgClassName='tw-max-h-10' />}
                </div>
            </div>
            <Button
                type='submit'
                color='primary'
                className='tw-mt-4 tw-mr-4'
                disabled={isSaveDisabled}
                loading={loading || logoLoading}
            >
                {t(COMMON_T_KEYS.BUTTONS.SAVE)}
            </Button>
            {!dirty && reloadRequired &&
                <div className='tw-mt-2 tw-text-warning'>{t(T_KEYS.RELOAD_PAGE_MESSAGE)}</div>}
            <UnsavedChangesLeavePrompt values={formValues} initialValues={initialValues} />
        </Form>
    );
};

export const AccountSettings: React.FC = () => {
    const [reloadRequired, setReloadRequired] = useState(false);
    const localUser = useMemo(() => userAuthUtils.getLocalUser(), []);
    const {send: getSettings, data: settings, loading: getLoading} = useApiData(settingsApi.getCommonSettings, {loadingDefault: true});

    const initialValues: AccountSettingsFormFields = useMemo(() => ({
        id: localUser.accountId,
        name: localUser.accountName,
        timeZone: settings?.timeZone,
        dateFormat: settings?.dateFormat,
        timeFormat: settings?.timeFormat,
        language: settings?.language,
        logo: settings?.logo
    }), [localUser, settings]);

    const onSaveSettingsSuccess = useCallback(() => {
        setReloadRequired(true);
        getSettings();
    }, [getSettings]);

    const {
        send: saveSettings,
        loading: saveInProgress,
    } = useApiData(settingsApi.saveCommonSettings, {onSuccess: onSaveSettingsSuccess, notificationType: PredefinedNotifierType.Save});

    const handleSubmit = useCallback((formValues: AccountSettingsFormFields) => {
        const accountData: Partial<AccountSettingsFormFields> = transform(values(ACCOUNT_FIELDS), (acc, key) => {
            if (key === ACCOUNT_FIELDS.ID) {
                return;
            }
            const value = get(formValues, key);
            if (value !== get(initialValues, key)) {
                set(acc, key, value);
            }
        }, {});

        saveSettings(accountData);
    }, [initialValues, saveSettings]);

    useEffect(() => {
        getSettings();
    }, [getSettings]);

    return (
        <div className='tw-relative'>
            <DataWrapper isLoading={getLoading || saveInProgress}>
                <Formik
                    initialValues={initialValues}
                    enableReinitialize
                    onSubmit={handleSubmit}
                >
                    {(props) => (
                        <FormTemplate
                            {...props}
                            reloadRequired={reloadRequired}
                            loading={getLoading || saveInProgress}
                        />
                    )}
                </Formik>
            </DataWrapper>
        </div>
    );
};